import type { CargoItem } from '@/types/cargoList.types';
import { formatCargoSize, formatDateRange, formatDateTime } from '@/utils/cargoListUtils';
import { Calendar, MapPin, Users, Clock, ChevronRight, Package } from 'lucide-react';
import { VesselBadge } from '@/components/fixtures/VesselBadge';
import { CargoSegmentBadge } from './CargoSegmentBadge';

interface CargoListItemProps {
  item: CargoItem;
  onClick?: (item: CargoItem) => void;
}

/**
 * Cargo list item component - displays cargo in a clean row/card format
 * Charterer | Laycan | Cargo Size | Trade Route | Primary Broker | Date/Time | Arrow
 *
 * The segment badge sits in the charterer column rather than taking a column of
 * its own: that column is the only one visible at every breakpoint, so the
 * vessel class survives on a phone, where the size column does not.
 *
 * Below `sm` the columns are gone and the row would otherwise carry nothing but
 * the charterer and a chevron, so the three facts that identify a cargo - laycan,
 * size, trade - fold into a single sub-line under the name instead.
 */
export function CargoListItem({ item, onClick }: CargoListItemProps) {
  const handleClick = () => {
    onClick?.(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(item);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="group flex cursor-pointer items-center rounded-lg border border-gray-200 bg-white px-3 py-2 transition-all hover:bg-gray-50 dark:border-dark-700 dark:bg-dark-800 dark:hover:bg-dark-700 sm:px-4 sm:py-2.5"
    >
      {/* Charterer Column */}
      <div className="min-w-0 flex-1 pr-2 sm:pr-4">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 sm:gap-x-2">
          <span className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
            {item.charterer}
          </span>
          {/* Vessel class from the view's Segment column */}
          <CargoSegmentBadge segment={item.segment} />
          {/* Vessel Badge - matching Angular implementation */}
          <VesselBadge
            onHoldWithCargo={item.isOnHold}
            onHoldWithCargoLabel="On Hold"
            statusPossible={item.status === 'Possible'}
            privacyStealth={item.privacyLevel === 'Stealth'}
            privacySuperSensitive={item.privacyLevel === 'Super Sensitive'}
          />
        </div>

        {/* Phone-only summary standing in for the hidden columns */}
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 sm:hidden">
          <span className="whitespace-nowrap">{formatDateRange(item.lcStart, item.lcEnd)}</span>
          <span aria-hidden="true">•</span>
          <span className="whitespace-nowrap">{formatCargoSize(item.cargoSize)}</span>
          {item.trade && (
            <>
              <span aria-hidden="true">•</span>
              <span className="truncate">{item.trade}</span>
            </>
          )}
        </div>

        <span className="hidden text-sm text-gray-500 dark:text-gray-400 sm:inline">Charterer</span>
      </div>

      {/* Divider */}
      <div className="hidden h-8 w-px flex-shrink-0 bg-gray-200 dark:bg-dark-600 sm:block" />

      {/* Laycan Column */}
      <div className="hidden flex-1 px-4 sm:block">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            {formatDateRange(item.lcStart, item.lcEnd)}
          </span>
        </div>
        <span className="ml-6 text-sm text-gray-500 dark:text-gray-400">Laycan</span>
      </div>

      {/* Divider */}
      <div className="hidden h-8 w-px flex-shrink-0 bg-gray-200 dark:bg-dark-600 md:block" />

      {/* Cargo Size Column */}
      <div className="hidden flex-1 px-4 md:block">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            {formatCargoSize(item.cargoSize)}
          </span>
        </div>
        <span className="ml-6 text-sm text-gray-500 dark:text-gray-400">Cargo Size</span>
      </div>

      {/* Divider */}
      <div className="hidden h-8 w-px flex-shrink-0 bg-gray-200 dark:bg-dark-600 lg:block" />

      {/* Trade Route Column */}
      <div className="hidden flex-1 px-4 lg:block">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">{item.trade}</span>
        </div>
        <span className="ml-6 text-sm text-gray-500 dark:text-gray-400">Trade Route</span>
      </div>

      {/* Divider */}
      <div className="hidden h-8 w-px flex-shrink-0 bg-gray-200 dark:bg-dark-600 xl:block" />

      {/* Primary Broker Column */}
      <div className="hidden flex-1 px-4 xl:block">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            {item.primaryBroker || '-'}
          </span>
        </div>
        <span className="ml-6 text-sm text-gray-500 dark:text-gray-400">Primary Broker</span>
      </div>

      {/* Divider */}
      <div className="hidden h-8 w-px flex-shrink-0 bg-gray-200 dark:bg-dark-600 2xl:block" />

      {/* Date/Time Column */}
      <div className="hidden flex-1 pl-4 2xl:block">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 flex-shrink-0 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            {formatDateTime(item.reportedTime).date}
          </span>
        </div>
        <span className="ml-6 text-sm text-gray-500 dark:text-gray-400">
          {formatDateTime(item.reportedTime).time}
        </span>
      </div>

      {/* Arrow */}
      <div className="ml-auto flex-shrink-0 pl-2 sm:ml-0 sm:pl-4">
        <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-600 dark:group-hover:text-gray-300 sm:h-5 sm:w-5" />
      </div>
    </div>
  );
}
